import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { AxelInterface } from '@pdm/axel-ui-components';
import type { ChatMessage, Message } from '@pdm/axel-ui-components';
import { useAuth } from '../contexts/AuthContext';
import { useSessions } from '../hooks/useSessions';
import { useChat } from '../hooks/useChat';

// Get API root from environment
const apiRoot = process.env.REACT_APP_API_ROOT || '';

// Helper to convert API Message to ChatMessage
function convertToChatMessage(msg: Message): ChatMessage {
  return {
    id: msg.messageId,
    role: msg.role,
    content: msg.content,
    timestamp: new Date(msg.createdAt),
    status: 'sent',
    metadata: msg.metadata,
    traceId: msg.traceId,
    feedbackValue: msg.feedbackValue,
    feedbackComment: msg.feedbackComment,
  };
}

const AxelPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams<{ sessionId?: string }>();
  const { user, canAccessAxel, jwtReady } = useAuth();

  const [inputValue, setInputValue] = useState('');

  // Read environment variable for text feedback feature
  const enableTextFeedback = process.env.REACT_APP_ENABLE_TEXT_FEEDBACK === 'true';

  // Use the real hooks (must be called before any early returns)
  const {
    sessions,
    loading: sessionsLoading,
    listSessions,
    createSession,
    updateSession,
    updateSessionLocal,
    deleteSession,
  } = useSessions();

  const {
    messages: apiMessages,
    sending,
    sendMessage,
    streamingContent,
    streamingStatus,
    statusSteps,
    submitFeedback,
    displayConfig,
    clearDisplayConfig,
  } = useChat({
    sessionID: sessionId || null,
    userId: user?.id?.toString() || user?.email || undefined,
    tags: ['vmto-app', process.env.REACT_APP_ENVIRONMENT || 'development'],
    onMetadataGenerated: (sessionID: string, title: string, description: string) => {
      // Update the session in local state when metadata is generated
      // Use updateSessionLocal to avoid redundant API call since backend already updated it
      console.log('[AxelPage] Metadata generated, updating local state:', {
        sessionID,
        title,
        description,
      });
      updateSessionLocal(sessionID, { title, description });
    },
  });

  // Debug logging for displayConfig
  useEffect(() => {
    console.log('[AxelPage] displayConfig prop:', displayConfig);
    if (displayConfig) {
      console.log('[AxelPage] displayConfig type:', displayConfig.type);
      console.log('[AxelPage] displayConfig details:', JSON.stringify(displayConfig, null, 2));
    }
  }, [displayConfig]);

  // Load sessions only after JWT is ready
  useEffect(() => {
    if (jwtReady && canAccessAxel) {
      listSessions();
    }
  }, [jwtReady, canAccessAxel, listSessions]);

  // If user doesn't have access, show access denied
  if (!canAccessAxel) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          padding: '24px',
        }}
      >
        <ExclamationCircleOutlined
          style={{ fontSize: '64px', color: '#faad14', marginBottom: '24px' }}
        />
        <h1 style={{ fontSize: '24px', marginBottom: '16px' }}>Access Denied</h1>
        <p style={{ fontSize: '16px', color: '#8c8c8c', textAlign: 'center', maxWidth: '400px' }}>
          You don&apos;t have permission to access Axel. Please contact your administrator if you
          believe this is an error.
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: '24px',
            padding: '8px 16px',
            fontSize: '14px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
            background: 'white',
            cursor: 'pointer',
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  // Show loading while JWT token is being exchanged
  if (!jwtReady) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          padding: '24px',
        }}
      >
        <div style={{ fontSize: '16px', color: '#8c8c8c' }}>Initializing Axel...</div>
      </div>
    );
  }

  // Convert API messages to ChatMessage format
  const messages: ChatMessage[] = apiMessages.map(convertToChatMessage);

  // If we're sending (waiting for response) or streaming, add a temporary assistant message
  // Show streamingStatus if available (ephemeral status messages), otherwise show content
  const displayMessages: ChatMessage[] = sending
    ? [
        ...messages,
        {
          id: 'streaming-temp',
          role: 'MESSAGE_ROLE_ASSISTANT' as const,
          content: streamingStatus || streamingContent, // Status replaces during processing, then shows content
          timestamp: new Date(),
          status: 'streaming' as const, // Shows Axel logo + spinner
        },
      ]
    : messages;

  // Determine current view from URL
  let currentView: 'sessions' | 'chat' | 'tasks' = 'sessions';
  if (location.pathname === '/axel/tasks') {
    currentView = 'tasks';
  } else if (sessionId) {
    currentView = 'chat';
  }

  const handleSend = async () => {
    if (!inputValue.trim() || sending) return;

    // If no session, create one first
    let activeSessionId = sessionId;
    if (!activeSessionId || activeSessionId === 'new') {
      try {
        const newSession = await createSession(inputValue.substring(0, 50)); // Use first 50 chars as title
        activeSessionId = newSession.sessionId;
        navigate(`/axel/chat/${activeSessionId}`);
      } catch (error) {
        console.error('Failed to create session:', error);
        return;
      }
    }

    try {
      await sendMessage(inputValue);
      setInputValue('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleNewChat = async () => {
    try {
      const newSession = await createSession('New Chat');
      navigate(`/axel/chat/${newSession.sessionId}`);
    } catch (error) {
      console.error('Failed to create new chat:', error);
    }
  };

  const handleSessionSelect = (id: string) => {
    navigate(`/axel/chat/${id}`);
  };

  const handleSessionUpdate = async (
    id: string,
    updates: { title?: string; description?: string }
  ) => {
    try {
      await updateSession(id, updates);
      message.success('Chat session updated successfully');
    } catch (error) {
      message.error('Failed to update chat session');
      console.error('Failed to update session:', error);
      throw error;
    }
  };

  const handleSessionDelete = (id: string) => {
    const session = sessions.find(s => s.sessionId === id);
    if (!session) return;

    Modal.confirm({
      title: 'Delete Chat Session?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${session.title}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteSession(id);
          message.success('Chat session deleted successfully');
          // If we're currently viewing this session, navigate away
          if (sessionId === id) {
            navigate('/axel');
          }
        } catch (error) {
          message.error('Failed to delete chat session');
          console.error('Failed to delete session:', error);
        }
      },
    });
  };

  // Feedback handler - uses submitFeedback from useChat hook
  // This now properly routes through the SDK which handles both DynamoDB and Langfuse
  const handleFeedback = async (
    messageId: string,
    feedback: { value: number; comment?: string }
  ) => {
    console.log('[AxelPage] Feedback submitted:', { messageId, feedback });

    try {
      await submitFeedback(messageId, feedback.value, feedback.comment);
      // Success toast is handled by FeedbackButtons component
    } catch (error) {
      console.error('[AxelPage] Failed to submit feedback:', error);
      message.error('Failed to submit feedback');
      throw error;
    }
  };

  // Convert API sessions to the format expected by AxelInterface
  const formattedSessions = sessions.map(s => ({
    id: s.sessionId,
    title: s.title,
    description: s.description,
    createdAt: new Date(s.createdAt),
    updatedAt: new Date(s.updatedAt),
    lastMessage: '', // We don't have this in the API response yet
    messageCount: s.messageCount,
  }));

  // Workflow tasks - populated based on LLM conversation context
  const workflowTasks: { id: string; title: string; roi: number }[] = [];

  return (
    <AxelInterface
      sessions={formattedSessions}
      messages={displayMessages}
      workflowTasks={workflowTasks}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSend={handleSend}
      onNewChat={handleNewChat}
      onSessionSelect={handleSessionSelect}
      onSessionUpdate={handleSessionUpdate}
      onSessionDelete={handleSessionDelete}
      onTaskListClick={() => navigate('/axel/tasks')}
      onBack={() => navigate('/axel')}
      onStartTask={taskId => console.log('Start task:', taskId)}
      currentView={currentView}
      activeSessionId={sessionId}
      loading={sending || sessionsLoading}
      showTaskList={false}
      chatTitle="Chat with Axel"
      chatSubtitle="AI Assistant for Product Data Management"
      chatPlaceholder="Ask Axel!"
      enableTextFeedback={enableTextFeedback}
      onFeedback={handleFeedback}
      statusSteps={statusSteps}
      enableReplitIntegration
      apiBaseUrl={apiRoot}
      accessToken={user?.token}
      displayConfig={displayConfig}
      onCloseRightPane={() => {
        console.log('[AxelPage] Clearing displayConfig via onCloseRightPane');
        clearDisplayConfig();
      }}
    />
  );
};

export default AxelPage;
